import { FC, useEffect, useState } from "react";
import styles from "./schoolForm.module.css";
import { Buttons } from "../..";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import UseCreateSchool from "../../../api/useCreateSchool";
import UseUpdateSchool from "../../../api/useUpdateSchool";
import { GetSchools_getSchools } from "./../../../__generated__/gql-types/GetSchools";
import { SchoolsFormSchema, initialValuesObj } from "./SchoolForm.types";
import { SchoolInput } from "../../../__generated__/gql-types/globalTypes";
import {
  errorNotification,
  successNotification,
  updatedNotification,
} from "./utils";
import { ActivityIndicator } from "../../ActivityIndicator";
import { Checkbox, Col, notification, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { GetAxes_getAxes } from "../../../__generated__/gql-types/GetAxes";
import { GetThemes_getThemes } from "../../../__generated__/gql-types/GetThemes";
import { useAxes } from "../../../hooks/useAxes";
import { useThemes } from "../../../hooks/useThemes";
import UseGetSchoolById from "../../../api/useGetSchoolById";
import { useSchools } from "../../../hooks/useSchools";
import { useDispatch } from "react-redux";
import { GetSchoolById_getSchoolById } from "../../../__generated__/gql-types/GetSchoolById";
import { useMonitoring } from "../../../hooks/useMonitoring";
import { useSelector } from "react-redux";
import { USER_AUTH } from "../../../store/selectors";

const { Option } = Select;

const SchoolsForm: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const monitor = useMonitoring();
  const user = useSelector(USER_AUTH);
  const { loading: loadingSchools, schools } = useSchools();
  const school = location.state as GetSchoolById_getSchoolById;
  const createSchool = UseCreateSchool();
  const updateSchool = UseUpdateSchool();
  const navigate = useNavigate();
  const getSchoolById = UseGetSchoolById(school?.id);

  const [loading, setloading] = useState(false);
  const [initialValues, setInitialValues] =
    useState<SchoolInput>(initialValuesObj);
  const [checks, setChecks] = useState({
    showSepPages: true,
    showGradesOnBase100: false,
  });
  const { axes, loading: loadingAxes } = useAxes();
  const { themes, loading: loadingThemes } = useThemes();

  const fetchInfo = async () => {
    setloading(true);
    try {
      if (school?.id) {
        const response = await getSchoolById();

        if (response) {
          setChecks({
            showSepPages: !!response?.show_sep_pages,
            showGradesOnBase100: !!response?.show_grades_on_base_100,
          });
          const nextInitialValues = {
            cct: response?.cct ?? "",
            name: response?.name ?? "",
            city: response?.city ?? "",
            show_sep_pages: response?.show_sep_pages,
            show_grades_on_base_100: response?.show_grades_on_base_100,
            view_english: response?.view_english,
            view_english_2526: response?.view_english_2526,
            view_english_2526_spark: response?.view_english_2526_spark,
            view_literacy: response?.view_literacy,
            view_NEM: response?.view_NEM,
            view_NEM_student: response?.view_NEM_student,
            view_2017_student: response?.view_2017_student,
            view_project_generator: response?.view_project_generator,
            view_reading_spanish: response?.view_reading_spanish,
            view_reading_english: response?.view_reading_english,
            view_dynamics: response?.view_dynamics,
            view_library_english: response?.view_library_english,
            view_library_spanish: response?.view_library_spanish,
            view_shop: response?.view_shop,
            is_active: response?.is_active,
            view_2526: response?.view_2526,
            view_2526_student: response?.view_2526_student,
            view_nem_without_science: response?.view_nem_without_science,
            view_math_fibonacci: response?.view_math_fibonacci,
            view_math_descartes: response?.view_math_descartes,
            view_gramatica: response?.view_gramatica,
            view_pcientifico: response?.view_pcientifico,
            view_historia: response?.view_historia,
            view_civica: response?.view_civica,
            view_geografia: response?.view_geografia,
            view_biologia: response?.view_biologia,
            view_quimica: response?.view_quimica,
            view_fisica: response?.view_fisica,
            view_espanol: response?.view_espanol,
            view_videopdf: response?.view_videopdf,
            view_chat: response?.view_chat,
            view_reportes: response?.view_reportes,
            show_tournament: response?.show_tournament,
            show_primary: response?.show_primary,
            show_secondary: response?.show_secondary,
            show_vocabulary: response?.show_vocabulary,
            excluded_axes:
              response?.excluded_axes?.map((axis: any) => {
                return { id: Number(axis.id) };
              }) ?? [],
            excluded_themes:
              response?.excluded_themes?.map((theme: any) => {
                return { id: Number(theme.id) };
              }) ?? [],
          } as SchoolInput;

          if (
            nextInitialValues.view_math_fibonacci &&
            nextInitialValues.view_math_descartes
          ) {
            nextInitialValues.view_math_descartes = false;
          }

          if (
            nextInitialValues.view_english &&
            nextInitialValues.view_english_2526_spark
          ) {
            nextInitialValues.view_english_2526_spark = false;
          }

          setInitialValues(nextInitialValues);
        }
      }
      setloading(false);
    } catch (error: any) {
      setloading(false);
      notification["error"]({
        message: "Error",
        description:
          "La información no pudo ser cargada, por favor notifica éste error!",
        placement: "top",
        duration: 3,
      });
      throw new Error(error);
    }
  };

  useEffect(() => {
    fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeCheckBox = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    const unChecked = !e.target.checked;
    const showSepPages = e.target.name === "showSepPages";
    const showGradesOnBase100 = e.target.name === "showGradesOnBase100";

    setChecks(
      checked && showSepPages
        ? { ...checks, showSepPages: true }
        : checked && showGradesOnBase100
        ? { ...checks, showGradesOnBase100: true }
        : unChecked && showSepPages
        ? { ...checks, showSepPages: false }
        : unChecked && showGradesOnBase100
        ? { ...checks, showGradesOnBase100: false }
        : checks
    );
  };

  const handleChangeAxes = (values: string[]) => {
    const axesValues = values!.map((value) => {
      let id = value.split("-")[1] !== undefined ? value.split("-")[0] : null;
      let name =
        value.split("-")[1] !== undefined ? value.split("-")[1] : value;

      const axe = axes!.find((axis) => axis?.id === id);

      if (axe) {
        return axe;
      } else {
        return {
          id,
          name,
        };
      }
    });

    initialValues.excluded_axes = axesValues.map((axis) => {
      return {
        id: Number(axis.id),
      };
    });
    setInitialValues(initialValues);
  };

  const handleChangeThemes = (values: string[]) => {
    const themesValues = values!.map((value) => {
      let id = value.split("-")[1] !== undefined ? value.split("-")[0] : null;
      let name =
        value.split("-")[1] !== undefined ? value.split("-")[1] : value;

      const selectedTheme = themes!.find((th) => th?.id === value);

      if (selectedTheme) {
        return selectedTheme;
      } else {
        return {
          id,
          name,
        };
      }
    });

    initialValues.excluded_themes = themesValues.map((theme) => {
      return {
        id: Number(theme.id),
      };
    });
    setInitialValues(initialValues);
  };

  const handleSubmit = async (values: SchoolInput) => {
    setloading(true);
    try {
      if (school?.id) {
        // Defensive guard: ensure mutual exclusivity before sending
        let view_math_fibonacci_fixed = !!initialValues.view_math_fibonacci;
        let view_math_descartes_fixed = !!initialValues.view_math_descartes;
        if (view_math_fibonacci_fixed && view_math_descartes_fixed) {
          view_math_descartes_fixed = false;
        }
        let view_english_fixed = !!initialValues.view_english;
        let view_english_spark_fixed = !!initialValues.view_english_2526_spark;
        if (view_english_fixed && view_english_spark_fixed) {
          view_english_spark_fixed = false;
        }
        const schoolInputCreate: SchoolInput = {
          cct: values.cct,
          name: values.name,
          city: values.city,
          show_sep_pages: checks.showSepPages,
          show_grades_on_base_100: checks.showGradesOnBase100,
          excluded_axes: initialValues.excluded_axes,
          excluded_themes: initialValues.excluded_themes,
          view_english: view_english_fixed,
          view_english_2526: initialValues.view_english_2526,
          view_english_2526_spark: view_english_spark_fixed,
          view_literacy: initialValues.view_literacy,
          view_NEM: initialValues.view_NEM,
          view_NEM_student: initialValues.view_NEM_student,
          view_2017_student: initialValues.view_2017_student,
          view_project_generator: initialValues.view_project_generator,
          view_reading_spanish: initialValues.view_reading_spanish,
          view_reading_english: initialValues.view_reading_english,
          view_shop: initialValues.view_shop,
          view_dynamics: initialValues.view_dynamics,
          view_library_english: initialValues.view_library_english,
          view_library_spanish: initialValues.view_library_spanish,
          is_active: initialValues.is_active,
          view_2526: initialValues.view_2526,
          view_2526_student: initialValues.view_2526_student,
          view_nem_without_science: initialValues.view_nem_without_science,
          show_tournament: initialValues.show_tournament,
          show_primary: initialValues.show_primary,
          show_secondary: initialValues.show_secondary,
          show_vocabulary: initialValues.show_vocabulary,
          view_math_fibonacci: view_math_fibonacci_fixed,
          view_math_descartes: view_math_descartes_fixed,
          view_gramatica: initialValues.view_gramatica,
          view_pcientifico: initialValues.view_pcientifico,
          view_historia: initialValues.view_historia,
          view_civica: initialValues.view_civica,
          view_geografia: initialValues.view_geografia,
          view_biologia: initialValues.view_biologia,
          view_quimica: initialValues.view_quimica,
          view_fisica: initialValues.view_fisica,
          view_espanol: initialValues.view_espanol,
          view_videopdf: initialValues.view_videopdf,
          view_chat: initialValues.view_chat,
          view_reportes: initialValues.view_reportes,
        };
        monitor.captureMessage("Update school", "info", { idSchool: school.id, userEmail: user?.email, ...schoolInputCreate });
        const response = await updateSchool({
          schoolInput: schoolInputCreate,
          updateSchoolId: school.id,
        });
        response.data.updateSchool.id && updatedNotification();

        let index = schools.findIndex(
          (school) => school.id === response.data.updateSchool.id
        );
        let schoolsUpdated = [...schools];
        schoolsUpdated[index] = response.data
          .updateSchool as unknown as GetSchools_getSchools;
        dispatch({ type: "data/setSchools", payload: schoolsUpdated });

        setloading(false);
        return navigate(-1);
      } else {
        values.show_sep_pages = checks.showSepPages;
        values.show_grades_on_base_100 = checks.showGradesOnBase100;
        if (!values.excluded_axes?.length || !values.excluded_themes?.length) {
          values.excluded_axes = undefined;
          values.excluded_themes = undefined;
        }

        // Defensive guard: ensure mutual exclusivity before sending
        let view_math_fibonacci_fixed = !!initialValues.view_math_fibonacci;
        let view_math_descartes_fixed = !!initialValues.view_math_descartes;
        if (view_math_fibonacci_fixed && view_math_descartes_fixed) {
          view_math_descartes_fixed = false;
        }
        // English Thrive vs Spark exclusivity
        let view_english_fixed = !!initialValues.view_english;
        let view_english_spark_fixed = !!initialValues.view_english_2526_spark;
        if (view_english_fixed && view_english_spark_fixed) {
          view_english_spark_fixed = false;
        }
        const schoolInput: SchoolInput = {
          cct: values.cct,
          name: values.name,
          city: values.city,
          show_sep_pages: checks.showSepPages,
          show_grades_on_base_100: checks.showGradesOnBase100,
          excluded_axes: initialValues.excluded_axes,
          excluded_themes: initialValues.excluded_themes,
          view_english: view_english_fixed,
          view_english_2526: false,
          view_english_2526_spark: view_english_spark_fixed,
          view_literacy: initialValues.view_literacy,
          view_NEM: initialValues.view_NEM,
          view_NEM_student: initialValues.view_NEM_student,
          view_2017_student: initialValues.view_2017_student,
          view_project_generator: initialValues.view_project_generator,
          view_reading_spanish: initialValues.view_reading_spanish,
          view_reading_english: initialValues.view_reading_english,
          view_shop: initialValues.view_shop,
          view_dynamics: initialValues.view_dynamics,
          view_library_english: initialValues.view_library_english,
          view_library_spanish: initialValues.view_library_spanish,
          is_active: initialValues.is_active,
          view_2526: initialValues.view_2526,
          view_2526_student: initialValues.view_2526_student,
          view_nem_without_science: initialValues.view_nem_without_science,
          show_tournament: initialValues.show_tournament,
          show_primary: initialValues.show_primary,
          show_secondary: initialValues.show_secondary,
          show_vocabulary: initialValues.show_vocabulary,
          view_math_fibonacci: view_math_fibonacci_fixed,
          view_math_descartes: view_math_descartes_fixed,
          view_gramatica: initialValues.view_gramatica,
          view_pcientifico: initialValues.view_pcientifico,
          view_historia: initialValues.view_historia,
          view_civica: initialValues.view_civica,
          view_geografia: initialValues.view_geografia,
          view_biologia: initialValues.view_biologia,
          view_quimica: initialValues.view_quimica,
          view_fisica: initialValues.view_fisica,
          view_espanol: initialValues.view_espanol,
          view_videopdf: initialValues.view_videopdf,
          view_chat: initialValues.view_chat,
          view_reportes: initialValues.view_reportes,
        };

        const createResponse = await createSchool(schoolInput);
        createResponse && successNotification();

        let newSchools = [];
        let newSchool = createResponse.data
          .createSchool as unknown as GetSchools_getSchools;
        newSchools = [newSchool, ...schools];
        dispatch({ type: "data/setSchools", payload: newSchools });

        setloading(false);
        return navigate(-1);
      }
    } catch (error) {
      setloading(false);
      errorNotification();
    }
  };

  const axeSelectChildren: React.ReactNode[] = [];
  axes?.forEach((axe: GetAxes_getAxes, index: number) => {
    axeSelectChildren.push(
      <Option value={`${axe?.id}-${axe.name}`} key={index + `${new Date()}`}>
        {axe?.name}
      </Option>
    );
  });

  if (loading || loadingSchools || loadingAxes || loadingThemes) {
    return <ActivityIndicator />;
  }

  return (
    <div className={styles.container}>
      {school?.id ? <h1>Editar Escuela </h1> : <h1>Nueva Escuela </h1>}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={SchoolsFormSchema}
        onSubmit={handleSubmit}
      >
        {({ handleReset }) => {
          return (
            <Form className={styles.formContainer}>
              {/* CCT */}
              <label htmlFor={"cct"}>{"CCT"}</label>
              <Field
                className={styles.formFields}
                id={"cct"}
                name={"cct"}
                type={"text"}
              ></Field>
              <ErrorMessage
                name={"cct"}
                component="div"
                className={styles.error}
              />

              {/* Name */}
              <label htmlFor={"name"}>{"Nombre"}</label>
              <Field
                className={styles.formFields}
                id={"name"}
                name={"name"}
                type={"text"}
              ></Field>
              <ErrorMessage
                name={"name"}
                component="div"
                className={styles.error}
              />

              {/* City */}
              <label htmlFor={"city"}>{"Ciudad"}</label>
              <Field
                className={styles.formFields}
                id={"city"}
                name={"city"}
                type={"text"}
              ></Field>
              <ErrorMessage
                name={"city"}
                component="div"
                className={styles.error}
              />

              <label htmlFor="axes">Eje(s) Excluído(s)</label>
              <Select
                mode="tags"
                className={styles.formFieldSelect}
                onChange={handleChangeAxes}
                defaultValue={
                  school?.id
                    ? school.excluded_axes?.map((axe, index: number) => {
                        return `${axe.id}-${axe.name}`;
                      })
                    : undefined
                }
              >
                {axeSelectChildren}
              </Select>
              <ErrorMessage
                name="axes"
                component="div"
                className={styles.error}
              />

              <label htmlFor="theme">Tema(s) Excluído(s)</label>
              <Select
                mode="tags"
                className={styles.formFieldSelect}
                defaultValue={
                  school?.id
                    ? school.excluded_themes?.map((theme) => {
                        return `${theme.id}-${theme.name}`;
                      })
                    : undefined
                }
                onChange={handleChangeThemes}
              >
                {themes?.map((theme: GetThemes_getThemes, index: number) => {
                  return (
                    <Option
                      value={`${theme?.id}-${theme?.name}`}
                      key={index + `${new Date()}`}
                    >
                      {theme?.name}
                    </Option>
                  );
                })}
              </Select>

              <Row>
                {/* ############## FLAGS DE ESCUELA ############## */}
                <Col span={12} style={{ marginTop: 20 }}>
                  <h1>Flags de Escuela</h1>
                  
        
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={handleChangeCheckBox}
                      name="showSepPages"
                      checked={checks.showSepPages}
                    >
                      Mostrar Páginas SEP
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={handleChangeCheckBox}
                      name="showGradesOnBase100"
                      checked={checks.showGradesOnBase100}
                    >
                      Mostrar Calificaciones en Base 100
                    </Checkbox>
                  </div>

                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_literacy: e.target.checked,
                        });
                      }}
                      name="showLiteracy"
                      checked={
                        typeof initialValues.view_literacy === "boolean"
                          ? initialValues.view_literacy
                          : false
                      }
                    >
                      Mostrar Lectoescritura (prof)
                    </Checkbox>
                  </div>
            

                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_reading_spanish: e.target.checked,
                          view_library_spanish: e.target.checked
                            ? true
                            : initialValues.view_library_spanish,
                        });
                      }}
                      name="show_reading_spanish"
                      checked={
                        typeof initialValues.view_reading_spanish === "boolean"
                          ? initialValues.view_reading_spanish
                          : false
                      }
                    >
                      Mostrar Lecturas español (esto afecta a toda la escuela,
                      tanto profe como alumno)
                    </Checkbox>
                  </div>

                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_reading_english: e.target.checked,
                          view_library_english: e.target.checked
                            ? true
                            : initialValues.view_library_english,
                        });
                      }}
                      name="show_reading_english"
                      checked={
                        typeof initialValues.view_reading_english === "boolean"
                          ? initialValues.view_reading_english
                          : false
                      }
                    >
                      Mostrar Lecturas inglés (esto afecta a toda la escuela,
                      tanto profe como alumno)
                    </Checkbox>
                  </div>

                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_library_spanish: e.target.checked,
                        });
                      }}
                      name="show_library_spanish"
                      checked={
                        typeof initialValues.view_library_spanish === "boolean"
                          ? initialValues.view_library_spanish
                          : false
                      }
                    >
                      Mostrar Biblioteca español (alumno)
                    </Checkbox>
                    
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_library_english: e.target.checked,
                        });
                      }}
                      name="show_library_english"
                      checked={
                        typeof initialValues.view_library_english === "boolean"
                          ? initialValues.view_library_english
                          : false
                      }
                    >
                      Mostrar Biblioteca inglés (alumno)
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_dynamics: e.target.checked,
                        });
                      }}
                      name="show_view_dynamics"
                      checked={
                        typeof initialValues.view_dynamics === "boolean"
                          ? initialValues.view_dynamics
                          : false
                      }
                    >
                      Mostrar Dinámicas Manipulativos
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          show_vocabulary: e.target.checked,
                        });
                      }}
                      name="show_show_vocabulary"
                      checked={
                        typeof initialValues.show_vocabulary === "boolean"
                          ? initialValues.show_vocabulary
                          : false
                      }
                    >
                      Mostrar Vocabulario
                    </Checkbox>
                  </div>
                  
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_videopdf: e.target.checked,
                        });
                      }}
                      name="show_view_videopdf"
                      checked={
                        typeof initialValues.view_videopdf === "boolean"
                          ? initialValues.view_videopdf
                          : false
                      }
                    >
                      Mostrar Clases alternas
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_reportes: e.target.checked,
                        });
                      }}
                      name="show_view_reportes"
                      checked={
                        typeof initialValues.view_reportes === "boolean"
                          ? initialValues.view_reportes
                          : false
                      }
                    >
                      Mostrar Analíticas
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_chat: e.target.checked,
                        });
                      }}
                      name="show_view_chat"
                      checked={
                        typeof initialValues.view_chat === "boolean"
                          ? initialValues.view_chat
                          : false
                      }
                    >
                      Mostrar Chat
                    </Checkbox>
                  </div>
                </Col>
                {/* ############################ */}

                {/* ############## FLAGS DE PROGRAMAS ############## */}
                <Col span={12} style={{ marginTop: 20 }}>
                  <h1>Flags de Programas</h1>
                  <h2 style={{ marginTop: 10 }}>Programa Luca:</h2>

{/* <h3 style={{ marginTop: 10 }}>2024 / 2025:</h3> */}
<div style={{ padding: "10px 0" }}>
  <Checkbox
    onChange={(e: CheckboxChangeEvent) => {
      setInitialValues({
        ...initialValues,
        view_NEM: e.target.checked,
      });
    }}
    name="showEnglish"
    checked={
      typeof initialValues.view_NEM === "boolean"
        ? initialValues.view_NEM
        : false
    }
  >
    Mostrar Programa Luca (prof)
  </Checkbox>
</div>
<div style={{ padding: "10px 0" }}>
  <Checkbox
    onChange={(e: CheckboxChangeEvent) => {
      setInitialValues({
        ...initialValues,
        view_NEM_student: e.target.checked,
      });
    }}
    name="showEnglish"
    checked={
      typeof initialValues.view_NEM_student === "boolean"
        ? initialValues.view_NEM_student
        : false
    }
  >
    Mostrar Programa Luca (alumno)
  </Checkbox>
</div>

  
                  <h1 style={{ marginTop: 20 }}>Flags de Asignaturas</h1>

                  <h2 style={{ marginTop: 20 }}>Inglés</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          // Thrive y Spark son excluyentes: si se activa Thrive, apagamos Spark
                          view_english: e.target.checked,
                          view_english_2526_spark: e.target.checked
                            ? false
                            : initialValues.view_english_2526_spark,
                        });
                      }}
                      name="showEnglish2526"
                      checked={
                        typeof initialValues.view_english === "boolean"
                          ? initialValues.view_english
                          : false
                      }
                    >
                      Mostrar Inglés Thrive (prof)
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          // Thrive y Spark son excluyentes: si se activa Spark, apagamos Thrive
                          view_english_2526_spark: e.target.checked,
                          view_english: e.target.checked
                            ? false
                            : initialValues.view_english,
                        });
                      }}
                      name="showEnglish2526Spark"
                      checked={
                        typeof initialValues.view_english_2526_spark ===
                        "boolean"
                          ? initialValues.view_english_2526_spark
                          : false
                      }
                    >
                      Mostrar Inglés Spark (prof)
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Matemáticas</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_math_fibonacci: e.target.checked,
                          view_math_descartes: e.target.checked ? false : initialValues.view_math_descartes
                        });
                      }}
                      name="view_math_fibonacci"
                      checked={typeof initialValues.view_math_fibonacci === "boolean" ? initialValues.view_math_fibonacci : false}
                    >
                      Matemática Fibonacci
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_math_descartes: e.target.checked,
                          view_math_fibonacci: e.target.checked ? false : initialValues.view_math_fibonacci
                        });
                      }}
                      name="view_math_descartes"
                      checked={typeof initialValues.view_math_descartes === "boolean" ? initialValues.view_math_descartes : false}
                    >
                      Matemática Descartes
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>P.cientifico</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_pcientifico: e.target.checked,
                        });
                      }}
                      name="view_pcientifico"
                      checked={typeof initialValues.view_pcientifico === "boolean" ? initialValues.view_pcientifico : false}
                    >
                      Mostrar P.científico
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Gramática</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_gramatica: e.target.checked,
                        });
                      }}
                      name="view_gramatica"
                      checked={typeof initialValues.view_gramatica === "boolean" ? initialValues.view_gramatica : false}
                    >
                      Mostrar Gramática
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Historia</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_historia: e.target.checked,
                        });
                      }}
                      name="view_historia"
                      checked={typeof initialValues.view_historia === "boolean" ? initialValues.view_historia : false}
                    >
                      Mostrar Historia
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Cívica</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_civica: e.target.checked,
                        });
                      }}
                      name="view_civica"
                      checked={typeof initialValues.view_civica === "boolean" ? initialValues.view_civica : false}
                    >
                      Mostrar Cívica
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Español</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_espanol: e.target.checked,
                        });
                      }}
                      name="view_espanol"
                      checked={typeof initialValues.view_espanol === "boolean" ? initialValues.view_espanol : false}
                    >
                      Mostrar Español
                    </Checkbox>
                  </div>

                  <h2 style={{ marginTop: 20 }}>Geografía</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_geografia: e.target.checked,
                        });
                      }}
                      name="view_geografia"
                      checked={typeof initialValues.view_geografia === "boolean" ? initialValues.view_geografia : false}
                    >
                      Mostrar Geografía
                    </Checkbox>
                  </div>
                  <h2 style={{ marginTop: 20 }}>Biología</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_biologia: e.target.checked,
                        });
                      }}
                      name="view_biologia"
                      checked={typeof initialValues.view_biologia === "boolean" ? initialValues.view_biologia : false}
                    >
                      Mostrar Biología
                    </Checkbox>
                  </div>
                  <h2 style={{ marginTop: 20 }}>Química</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_quimica: e.target.checked,
                        });
                      }}
                      name="view_quimica"
                      checked={typeof initialValues.view_quimica === "boolean" ? initialValues.view_quimica : false}
                    >
                      Mostrar Química
                    </Checkbox>
                  </div>
                  <h2 style={{ marginTop: 20 }}>Física</h2>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_fisica: e.target.checked,
                        });
                      }}
                      name="view_fisica"
                      checked={typeof initialValues.view_fisica === "boolean" ? initialValues.view_fisica : false}
                    >
                      Mostrar Física
                    </Checkbox>
                  </div>

                  


                  {/* <h3 style={{ marginTop: 10 }}>2025 / 2026:</h3>
                  <Row style={{ marginBottom: 5 }}>
                    Para seleccionar Programa 2526 sin Ciencias, debe dejar
                    marcada la casilla "Mostrar 2025/2026 (prof)" y "Mostrar
                    2025/2026 [sin Ciencias] (prof)" al mismo tiempo
                  </Row>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      name="showEnglish"
                      checked={
                        typeof initialValues.view_2526 === "boolean"
                          ? initialValues.view_2526
                          : false
                      }
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_2526: e.target.checked,
                          view_nem_without_science: e.target.checked ? initialValues.view_nem_without_science : false
                        });
                      }}
                    >
                      Mostrar 2025/2026 (prof)
                    </Checkbox>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      name="showEnglish"
                      checked={
                        typeof initialValues.view_nem_without_science ===
                        "boolean"
                          ? initialValues.view_nem_without_science
                          : false
                      }
                      onChange={(e: CheckboxChangeEvent) => {
                        if (initialValues.view_2526)
                          setInitialValues({
                            ...initialValues,
                            view_nem_without_science: e.target.checked,
                          });
                      }}
                    >
                      Mostrar 2025/2026 [sin Ciencias] (prof)
                    </Checkbox>
                  </div>

                  <div style={{ padding: "10px 0" }}>
                    <Checkbox
                      onChange={(e: CheckboxChangeEvent) => {
                        setInitialValues({
                          ...initialValues,
                          view_2526_student: e.target.checked
                        });
                      }}
                      name="showEnglish"
                      checked={
                        typeof initialValues.view_2526_student === "boolean"
                          ? initialValues.view_2526_student
                          : false
                      }
                    >
                      Mostrar 2025/2026 (alumno)
                    </Checkbox>
                  </div> */}
                </Col>
                {/* ############################ */}
              </Row>

              {/* Buttons */}
              <Buttons
                handleClick={() => {
                  handleReset();
                  navigate(-1);
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SchoolsForm;
